class PremiumCustomHeader extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.signal = this.controller.signal;
    this.drawer = this.querySelector('[data-pch-drawer]');
    this.drawerTrigger = this.querySelector('[data-pch-drawer-open]');
    this.menuDetails = [...this.querySelectorAll('[data-pch-menu-details]')];
    this.localeDetails = this.querySelector('[data-pch-locale]');
    this.cartTrigger = this.querySelector('[data-pch-cart-trigger]');
    this.cartDrawer = this.querySelector('[data-pch-cart-drawer]');
    this.cartPanel = this.querySelector('[data-pch-cart-panel]');
    this.cartItems = this.querySelector('[data-pch-cart-items]');
    this.cartGift = this.querySelector('[data-pch-cart-gift]');
    this.cartEmpty = this.querySelector('[data-pch-cart-empty]');
    this.cartFooter = this.querySelector('[data-pch-cart-footer]');
    this.cartStatus = this.querySelector('[data-pch-cart-status]');
    this.cartSubtotal = this.querySelector('[data-pch-cart-subtotal]');
    this.cartCount = this.querySelector('[data-pch-cart-count]');
    this.logoFlipStage = this.querySelector('.pch__logo-flip-stage.is-enabled');
    this.desktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.lastScrollY = window.scrollY;
    this.scrollFrame = 0;

    this.addEventListener('pointerdown', this.onPointerDown, { signal: this.signal });
    document.addEventListener('click', this.onDocumentClick, { signal: this.signal });
    document.addEventListener('keydown', this.onKeyDown, { signal: this.signal });
    document.addEventListener('theme-drawer:open', this.onThemeDrawerOpen, { signal: this.signal });
    document.addEventListener('theme-drawer:close', this.onThemeDrawerClose, { signal: this.signal });
    window.addEventListener('scroll', this.onScroll, { passive: true, signal: this.signal });
    window.addEventListener('resize', this.onResize, { passive: true, signal: this.signal });

    this.drawerTrigger?.addEventListener('click', this.openDrawer, { signal: this.signal });
    this.querySelector('[data-pch-drawer-close]')?.addEventListener('click', this.closeDrawer, { signal: this.signal });
    this.drawer?.addEventListener('click', this.onDrawerClick, { signal: this.signal });
    this.drawer?.addEventListener('cancel', this.onDrawerCancel, { signal: this.signal });
    this.drawer?.addEventListener('close', this.onDrawerClosed, { signal: this.signal });
    this.cartTrigger?.addEventListener('click', this.openCartDrawer, { signal: this.signal });
    this.querySelector('[data-pch-cart-close]')?.addEventListener('click', this.closeCartDrawer, { signal: this.signal });
    this.cartDrawer?.addEventListener('click', this.onCartDrawerClick, { signal: this.signal });
    this.cartDrawer?.addEventListener('cancel', this.onCartDrawerCancel, { signal: this.signal });
    this.cartDrawer?.addEventListener('close', this.onCartDrawerClosed, { signal: this.signal });
    this.cartItems?.addEventListener('click', this.onCartItemsClick, { signal: this.signal });
    this.querySelector('[data-pch-shiprocket-checkout]')?.addEventListener('click', this.onShiprocketCheckout, { signal: this.signal });
    this.menuDetails.forEach((details) => {
      details.addEventListener('mouseenter', () => this.openDesktopMenu(details), { signal: this.signal });
      details.addEventListener('mouseleave', () => this.scheduleDesktopMenuClose(details), { signal: this.signal });
      details.addEventListener('toggle', () => this.syncMenuState(details), { signal: this.signal });
    });

    this.resizeObserver = new ResizeObserver(() => this.updateHeaderHeight());
    this.resizeObserver.observe(this);
    this.updateHeaderHeight();
    this.startLogoFlip();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
    clearTimeout(this.drawerCloseTimer);
    clearTimeout(this.cartDrawerCloseTimer);
    clearInterval(this.logoFlipTimer);
    this.cartRequestController?.abort();
    this.menuDetails?.forEach((details) => clearTimeout(details._pchCloseTimer));
  }

  updateHeaderHeight() {
    const height = Math.round(this.getBoundingClientRect().height);
    document.body.style.setProperty('--header-height', height + 'px');
  }

  startLogoFlip() {
    if (!this.logoFlipStage || this.dataset.logoFlipEnabled !== 'true' || this.reducedMotion.matches) return;

    const seconds = Math.min(12, Math.max(3, Number(this.dataset.logoFlipInterval) || 5));
    this.logoFlipTimer = window.setInterval(() => {
      this.logoFlipStage.classList.toggle('is-flipped');
    }, seconds * 1000);
  }

  onPointerDown = (event) => {
    const target = event.target.closest('[data-pch-click]');
    if (!target || !this.contains(target) || this.reducedMotion.matches) return;

    const rect = target.getBoundingClientRect();
    const wave = document.createElement('span');
    wave.className = 'pch__click-wave';
    wave.style.left = event.clientX - rect.left + 'px';
    wave.style.top = event.clientY - rect.top + 'px';

    if (getComputedStyle(target).position === 'static') target.style.position = 'relative';
    target.append(wave);
    wave.addEventListener('animationend', () => wave.remove(), { once: true });
  };

  openDesktopMenu(details) {
    if (!this.desktopPointer.matches || window.innerWidth < 990) return;
    clearTimeout(details._pchCloseTimer);
    this.closeDesktopMenus(details);
    details.open = true;
  }

  scheduleDesktopMenuClose(details) {
    if (!this.desktopPointer.matches || window.innerWidth < 990) return;
    clearTimeout(details._pchCloseTimer);
    details._pchCloseTimer = setTimeout(() => {
      details.open = false;
    }, 130);
  }

  syncMenuState(details) {
    const summary = details.querySelector(':scope > summary');
    summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    if (details.open) this.closeDesktopMenus(details);
  }

  closeDesktopMenus(except = null) {
    this.menuDetails.forEach((details) => {
      if (details !== except) details.open = false;
    });
  }

  onDocumentClick = (event) => {
    if (!this.contains(event.target)) {
      this.closeDesktopMenus();
      if (this.localeDetails) this.localeDetails.open = false;
      return;
    }

    const activeDetails = event.target.closest('[data-pch-menu-details]');
    if (!activeDetails && !event.target.closest('[data-pch-locale]')) {
      this.closeDesktopMenus();
      if (this.localeDetails) this.localeDetails.open = false;
    }
  };

  onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.closeDesktopMenus();
    if (this.localeDetails) this.localeDetails.open = false;
    if (this.drawer?.open) this.closeDrawer();
    if (this.cartDrawer?.open) this.closeCartDrawer();
  };

  openDrawer = () => {
    if (!this.drawer) return;
    clearTimeout(this.drawerCloseTimer);
    this.drawer.classList.remove('is-closing');
    if (typeof this.drawer.showModal === 'function') {
      if (!this.drawer.open) this.drawer.showModal();
    } else {
      this.drawer.setAttribute('open', '');
    }
    this.drawerTrigger?.setAttribute('aria-expanded', 'true');
    this.classList.remove('is-header-hidden');
    requestAnimationFrame(() => this.drawer.querySelector('input, summary, a, button')?.focus({ preventScroll: true }));
  };

  closeDrawer = () => {
    if (!this.drawer?.open) return;
    this.drawer.classList.add('is-closing');
    clearTimeout(this.drawerCloseTimer);
    this.drawerCloseTimer = setTimeout(() => {
      if (typeof this.drawer.close === 'function') this.drawer.close();
      else this.drawer.removeAttribute('open');
    }, this.reducedMotion.matches ? 0 : 260);
  };

  onDrawerClick = (event) => {
    if (event.target === this.drawer) {
      this.closeDrawer();
      return;
    }

    const link = event.target.closest('a[href]');
    if (link && !link.closest('.pch__drawer-locale-form')) this.closeDrawer();
  };

  onDrawerCancel = (event) => {
    event.preventDefault();
    this.closeDrawer();
  };

  onDrawerClosed = () => {
    this.drawer?.classList.remove('is-closing');
    this.drawerTrigger?.setAttribute('aria-expanded', 'false');
    this.drawerTrigger?.focus({ preventScroll: true });
  };

  openCartDrawer = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.cartDrawer) return false;

    clearTimeout(this.cartDrawerCloseTimer);
    this.cartDrawer.classList.remove('is-closing');
    this.classList.remove('is-header-hidden');

    if (!this.cartDrawer.open) {
      if (typeof this.cartDrawer.showModal === 'function') this.cartDrawer.showModal();
      else this.cartDrawer.setAttribute('open', '');
    }

    document.documentElement.setAttribute('scroll-lock', '');
    this.cartTrigger?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => this.querySelector('[data-pch-cart-close]')?.focus({ preventScroll: true }));
    window.DesiDanaLoadShiprocket?.().catch(() => {});
    this.loadCart();
    return false;
  };

  closeCartDrawer = (event) => {
    event?.preventDefault();
    if (!this.cartDrawer?.open) return;

    this.cartDrawer.classList.add('is-closing');
    clearTimeout(this.cartDrawerCloseTimer);
    this.cartDrawerCloseTimer = setTimeout(() => {
      if (typeof this.cartDrawer.close === 'function') this.cartDrawer.close();
      else {
        this.cartDrawer.removeAttribute('open');
        this.onCartDrawerClosed();
      }
    }, this.reducedMotion.matches ? 0 : 260);
  };

  onCartDrawerClick = (event) => {
    if (event.target === this.cartDrawer) this.closeCartDrawer(event);
  };

  onCartDrawerCancel = (event) => {
    event.preventDefault();
    this.closeCartDrawer();
  };

  onCartDrawerClosed = () => {
    this.cartDrawer?.classList.remove('is-closing');
    document.documentElement.removeAttribute('scroll-lock');
    this.cartTrigger?.setAttribute('aria-expanded', 'false');
    this.cartTrigger?.focus({ preventScroll: true });
  };

  onShiprocketCheckout = async (event) => {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const offer = window.DesiDanaGheeOffer;
      let state;
      let checkoutCart;
      if (offer) {
        const { cart, warning } = await offer.sync();
        checkoutCart = cart;
        this.renderCart(cart, warning);
        state = offer.describe(cart);
        if (!cart.item_count) throw new Error('Your cart is empty. Please add a product first.');
        if (warning || !state.complete) throw new Error(warning || 'Your complimentary gift is not ready. Please contact us before checkout.');
      }
      await window.DesiDanaLoadShiprocket?.();
      if (typeof window.shiprocketCheckoutEvents?.buyCart === 'function') {
        window.shiprocketCheckoutEvents.buyCart(event);
        return;
      }
      // Do not route a charged gift to ordinary checkout: that discount lives in Shiprocket.
      if (state?.giftCharge) throw new Error('Shiprocket checkout is still loading. Please try again to receive your free gift.');
      if (checkoutCart && this.getPickleOfferSummary(checkoutCart).discount > 0) {
        throw new Error('Shiprocket checkout is still loading. Please try again to receive your pickle offer.');
      }
      window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
    } catch (error) {
      this.setCartStatus(error.message || 'Checkout could not start. Please try again.', true);
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1500);
    }
  };

  loadCart = async () => {
    this.cartRequestController?.abort();
    this.cartRequestController = new AbortController();
    this.setCartStatus('Loading your cart…');

    try {
      if (window.DesiDanaGheeOffer) {
        const controller = this.cartRequestController;
        const { cart, warning } = await window.DesiDanaGheeOffer.sync();
        if (!controller.signal.aborted) this.renderCart(cart, warning);
        return;
      }
      const cartUrl = `${this.dataset.cartUrl || '/cart'}.js`;
      const response = await fetch(cartUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: this.cartRequestController.signal,
      });
      if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
      this.renderCart(await response.json());
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.setCartStatus('We could not refresh your cart. Please try again.', true);
    }
  };

  onCartItemsClick = (event) => {
    const button = event.target.closest('[data-pch-cart-action]');
    if (!button || !this.cartItems?.contains(button)) return;

    const currentQuantity = Number(button.dataset.cartQuantity || 0);
    const action = button.dataset.pchCartAction;
    const nextQuantity = action === 'increase' ? currentQuantity + 1 : action === 'decrease' ? Math.max(0, currentQuantity - 1) : 0;
    this.changeCartItem(button.dataset.cartKey, nextQuantity);
  };

  changeCartItem = async (key, quantity) => {
    if (!key || this.cartPanel?.classList.contains('is-updating')) return;

    this.cartPanel?.classList.add('is-updating');
    this.cartPanel?.setAttribute('aria-busy', 'true');
    this.setCartStatus('Updating your cart…');

    try {
      if (window.DesiDanaGheeOffer) {
        const { cart, warning } = await window.DesiDanaGheeOffer.change(key, quantity);
        this.renderCart(cart, warning);
        return;
      }
      const route = this.dataset.cartChangeUrl || '/cart/change';
      const response = await fetch(route.endsWith('.js') ? route : `${route}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error(`Cart update failed (${response.status})`);
      this.renderCart(await response.json());
    } catch (error) {
      this.setCartStatus('That update did not complete. Please try again.', true);
    } finally {
      this.cartPanel?.classList.remove('is-updating');
      this.cartPanel?.removeAttribute('aria-busy');
    }
  };

  setCartStatus(message, isError = false) {
    if (!this.cartStatus) return;
    this.cartStatus.hidden = false;
    this.cartStatus.classList.toggle('is-error', isError);
    const label = this.cartStatus.querySelector('span:last-child');
    if (label) label.textContent = message;
  }

  getPickleOfferSummary(cart) {
    // Exact 325g variants selected in DESIDANABUY3 / DESIDANABUY4.
    // Do not match titles: that would incorrectly include 500g jars or chutneys.
    const variants = new Set(['47371091935420', '47370703765692', '47370740564156', '47370977607868', '47410731122876']);
    const lines = (cart.items || []).filter((item) => variants.has(String(item.variant_id || item.id)) && Number(item.quantity) > 0);
    const quantity = lines.reduce((sum, item) => sum + Number(item.quantity), 0);
    const empty = { quantity, discount: 0, bundleSize: 0, bundleCount: 0, code: '', target: 0 };
    if (cart.currency && cart.currency !== 'INR') return empty;
    // Shiprocket applies complete bundles to the highest-priced eligible units;
    // leftover jars keep their normal price. Only one of these two coupons wins.
    const sorted = lines.map((item) => ({
      quantity: Number(item.quantity),
      price: Math.max(0, Number(item.final_line_price ?? item.line_price ?? 0)) / Number(item.quantity),
    })).sort((a, b) => b.price - a.price);
    let best = empty;
    for (const [bundleSize, target] of [[3, 69900], [4, 84900]]) {
      const bundleCount = Math.floor(quantity / bundleSize);
      if (!bundleCount) continue;
      let remaining = bundleCount * bundleSize;
      let covered = 0;
      for (const line of sorted) {
        const used = Math.min(remaining, line.quantity);
        covered += used * line.price;
        remaining -= used;
      }
      const discount = Math.max(0, Math.round(covered - bundleCount * target));
      if (discount > best.discount) best = { quantity, discount, bundleSize, bundleCount, target, code: `DESIDANABUY${bundleSize}` };
    }
    // Cart-level Shopify discounts may overlap: don't estimate a second discount.
    if ((cart.cart_level_discount_applications || []).length) return empty;
    return best;
  }

  getCartOfferSummary(cart, warning = '') {
    const offer = window.DesiDanaGheeOffer;
    const state = offer?.describe(cart);
    const pickle = this.getPickleOfferSummary(cart);
    // These existing Shiprocket coupons are not configured to combine with BUY1LTR.
    // A mixed cart must not promise both discounts until combination is authorized.
    const combinationPending = Boolean(pickle.discount > 0 && state?.eligibleQuantity > 0 && state.giftQuantity > 0);
    const giftIncluded = Boolean(!warning && !combinationPending && state?.complete && state.eligibleQuantity > 0 && state.giftQuantity > 0);
    // Estimated Shiprocket subtotal, not a mutation of Shopify's underlying cart.
    // Subtract only the gift's remaining charge, avoiding a double discount if Shopify already made it free.
    const giftDiscount = giftIncluded ? Math.max(0, Number(state.giftCharge) || 0) : 0;
    const pickleDiscount = combinationPending ? 0 : pickle.discount;
    return { offer, state, giftIncluded, giftDiscount, pickle, pickleDiscount, combinationPending,
      subtotal: Math.max(0, (Number(cart.total_price) || 0) - giftDiscount - pickleDiscount) };
  }

  renderCart(cart, warning = '') {
    if (!this.cartItems || !this.cartEmpty || !this.cartFooter) return;

    const itemCount = Number(cart.item_count || 0);
    this.cartItems.replaceChildren();
    this.cartGift?.replaceChildren();
    if (this.cartGift) this.cartGift.hidden = true;
    this.cartStatus.hidden = true;
    this.cartStatus.classList.remove('is-error');
    if (this.cartCount) this.cartCount.textContent = itemCount;
    this.cartEmpty.hidden = itemCount !== 0;
    this.cartItems.hidden = itemCount === 0;
    this.cartFooter.hidden = itemCount === 0;

    const { offer, state, giftIncluded, subtotal, pickle, pickleDiscount, combinationPending } = this.getCartOfferSummary(cart, warning);
    if (this.cartSubtotal) this.cartSubtotal.textContent = this.formatCartMoney(subtotal, cart.currency);

    if (itemCount > 0) {
      const fragment = document.createDocumentFragment();
      if (pickleDiscount > 0 || combinationPending) {
        const message = document.createElement('p');
        message.className = 'pch-cart-gift__celebration';
        message.textContent = combinationPending
          ? 'Ghee and pickle offers do not combine. Your applicable offer and final total are confirmed at Shiprocket checkout.'
          : `${pickle.code}: ${pickle.bundleCount > 1 ? `${pickle.bundleCount} bundles of ` : ''}${pickle.bundleSize} × 325g pickles for ${this.formatCartMoney(pickle.target * pickle.bundleCount, cart.currency)}. You save ${this.formatCartMoney(pickleDiscount, cart.currency)} at Shiprocket checkout.`;
        fragment.append(message);
      }
      if (giftIncluded && this.cartGift) {
        const celebration = document.createElement('p');
        celebration.className = 'pch-cart-gift__celebration';
        celebration.textContent = 'Congratulations! 125 ml Ghee FREE';
        this.cartGift.append(celebration);
      }
      cart.items.forEach((item) => {
        if (offer?.isGift(item) && this.cartGift) {
          this.cartGift.hidden = false;
          this.cartGift.append(this.buildCartGift(item, cart.currency, giftIncluded));
        } else fragment.append(this.buildCartItem(item, cart.currency));
      });
      this.cartItems.append(fragment);
      if (giftIncluded && this.cartGift) {
        const note = document.createElement('p');
        note.className = 'pch-cart-gift__note';
        note.textContent = 'Your estimated subtotal includes the free-gift discount, applied at Shiprocket checkout.';
        this.cartGift.append(note);
      }
      if (state?.eligibleQuantity && !state.giftQuantity && !warning) {
        warning = '125 ml complimentary ghee is currently unavailable. Please contact us before checkout.';
      }
    }
    if (warning) this.setCartStatus(warning, true);
    this.syncCartBubble(itemCount);
  }

  buildCartGift(item, currency, giftIncluded = false) {
    const card = this.buildCartItem(item, currency);
    card.classList.add('pch-cart-gift');
    card.querySelector('.pch-cart-item__actions')?.remove();
    let variant = card.querySelector('.pch-cart-item__variant');
    if (!variant) {
      variant = document.createElement('p');
      variant.className = 'pch-cart-item__variant';
      card.querySelector('.pch-cart-item__content').append(variant);
    }
    variant.textContent = `125 ml · ${item.quantity} complimentary ${item.quantity === 1 ? 'jar' : 'jars'}`;
    card.querySelector('.pch-cart-item__price').textContent = giftIncluded || (item.final_line_price ?? item.line_price) === 0 ? 'FREE · ₹0' : 'Gift offer pending';
    // Gift-only items are not links to a separately purchasable size.
    card.querySelectorAll('a').forEach((link) => {
      const replacement = document.createElement('span');
      replacement.className = link.className;
      replacement.append(...link.childNodes);
      link.replaceWith(replacement);
    });
    return card;
  }

  buildCartItem(item, currency) {
    const card = document.createElement('article');
    card.className = 'pch-cart-item';

    const media = document.createElement('a');
    media.className = 'pch-cart-item__media';
    media.href = item.url || '#';
    const imageUrl = item.featured_image?.url || item.image;
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = item.featured_image?.alt || item.product_title || '';
      image.loading = 'lazy';
      image.width = 112;
      image.height = 112;
      media.append(image);
    }

    const content = document.createElement('div');
    content.className = 'pch-cart-item__content';
    const title = document.createElement('a');
    title.className = 'pch-cart-item__title';
    title.href = item.url || '#';
    title.textContent = item.product_title || item.title || 'Product';
    content.append(title);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('p');
      variant.className = 'pch-cart-item__variant';
      variant.textContent = item.variant_title;
      content.append(variant);
    }

    const price = document.createElement('strong');
    price.className = 'pch-cart-item__price';
    price.textContent = this.formatCartMoney(item.final_line_price ?? item.line_price, currency);
    content.append(price);

    const actions = document.createElement('div');
    actions.className = 'pch-cart-item__actions';
    const quantity = document.createElement('div');
    quantity.className = 'pch-cart-item__quantity';
    quantity.setAttribute('aria-label', `Quantity for ${item.product_title || item.title || 'product'}`);
    quantity.append(
      this.buildCartActionButton('decrease', item, 'Decrease quantity', '−'),
      Object.assign(document.createElement('span'), { textContent: String(item.quantity) }),
      this.buildCartActionButton('increase', item, 'Increase quantity', '+')
    );
    actions.append(quantity, this.buildCartActionButton('remove', item, 'Remove item', 'Remove'));
    content.append(actions);

    card.append(media, content);
    return card;
  }

  buildCartActionButton(action, item, label, text) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = action === 'remove' ? 'pch-cart-item__remove' : 'pch-cart-item__quantity-button';
    button.dataset.pchCartAction = action;
    button.dataset.cartKey = item.key;
    button.dataset.cartQuantity = String(item.quantity);
    button.setAttribute('aria-label', label);
    button.textContent = text;
    return button;
  }

  formatCartMoney(cents = 0, currency = 'INR') {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: currency || 'INR',
        maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
      }).format(Number(cents) / 100);
    } catch (error) {
      return `₹${(Number(cents) / 100).toFixed(2)}`;
    }
  }

  syncCartBubble(itemCount) {
    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', itemCount === 0);
      bubble.toggleAttribute('data-maintain-ratio', itemCount <= 99);
      const count = bubble.querySelector('.cart-bubble__text-count');
      if (count) {
        count.textContent = itemCount > 99 ? '99+' : String(itemCount);
        count.classList.toggle('hidden', itemCount === 0);
      }
    });
    document.querySelectorAll('.pch__cart-icon').forEach((icon) => icon.classList.toggle('pch__cart-icon--has-items', itemCount > 0));
  }

  onThemeDrawerOpen = (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    this.cartTrigger?.setAttribute('aria-expanded', 'true');
    this.classList.remove('is-header-hidden');
  };

  onThemeDrawerClose = (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    this.cartTrigger?.setAttribute('aria-expanded', 'false');
  };

  onScroll = () => {
    if (this.dataset.stickyMode !== 'scroll-up' || this.drawer?.open || this.cartDrawer?.open || this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      const currentY = Math.max(0, window.scrollY);
      const delta = currentY - this.lastScrollY;

      if (currentY <= this.offsetHeight || delta < -7) {
        this.classList.remove('is-header-hidden');
      } else if (delta > 7) {
        this.classList.add('is-header-hidden');
        this.closeDesktopMenus();
        if (this.localeDetails) this.localeDetails.open = false;
      }

      this.lastScrollY = currentY;
    });
  };

  onResize = () => {
    if (window.innerWidth >= 990 && this.drawer?.open) this.closeDrawer();
    if (window.innerWidth < 990) this.closeDesktopMenus();
  };
}

if (!customElements.get('premium-custom-header')) {
  customElements.define('premium-custom-header', PremiumCustomHeader);
}
